// Discipline criteria definitions for the Room Takeoff subsections.
// The DB (TakeoffCriteria, db/22) stores only key/value rows, so adding,
// removing, or re-labeling a field here is a frontend-only change — no
// migration needed. Fields with perRoom: true also appear in the per-room
// override table (blank = inherits the project-level default).
//
// scopeDiscipline maps each subsection to the Scope & RACI register's
// Discipline value — a subsection is "active" when CleanSpace holds the R
// mark on at least one non-intake item in that discipline, and the CS items
// are listed beneath the criteria as scope reference.

export const CRITERIA_DISCIPLINES = [
  {
    key: "arch",
    label: "Architectural",
    icon: "🏗️",
    scopeDiscipline: "Architectural",
    fields: [
      // Predominant/reference system only — a room routinely mixes systems
      // wall-by-wall (see roomScopeFormulas.js), so this doesn't drive a
      // quantity on its own; take off wall panel SF via "Draw condition" on
      // the canvas instead, per-wall.
      { key: "wall_system",    label: "Wall panel system", type: "select", csi: "13 21 13",
        options: ['uPVC 3" honeycomb', 'Aluminum honeycomb 2"', '1/2" liner panel (one side)', "MgO panel", "FRP", "Stick-built / drywall", "By others"] },
      { key: "ceiling_system", label: "Ceiling system", type: "select", csi: "13 21 13",
        options: ["Walkable panel", "Non-walkable panel", "Heavy grid 2×4", "Gel grid", "Hard-lid drywall", "By others"] },
      { key: "floor_finish",   label: "Floor finish", type: "select", csi: "09 67 00",
        options: ["Epoxy w/ integral cove", "Welded sheet vinyl w/ cove", "ESD vinyl", "Sealed concrete", "By others"] },
      { key: "coving",         label: "Coving", type: "select", csi: "09 67 00",
        options: ["Full (floor + ceiling)", "Floor only", "None"] },
      { key: "door_count",     label: "Personnel doors", type: "number", perRoom: true, csi: "08 11 00" },
      { key: "window_count",   label: "Vision panels", type: "number", perRoom: true, csi: "08 56 19" },
      { key: "pass_throughs",  label: "Pass-throughs", type: "number", perRoom: true, csi: "13 21 13" },
    ],
  },
  {
    key: "mech",
    label: "Mechanical / HVAC",
    icon: "🌀",
    scopeDiscipline: "Mechanical / HVAC",
    fields: [
      { key: "air_delivery",  label: "Air delivery", type: "select", csi: "23 30 00",
        options: ["Ducted HEPA", "FFU (fan filter units)", "Mixed"] },
      { key: "filter_type",   label: "Terminal filtration", type: "select", csi: "23 40 00",
        options: ["Room-side replaceable, gel seal", "Room-side replaceable, gasket seal", "Ducted terminal HEPA"] },
      { key: "duct_material", label: "Duct material", type: "select", csi: "23 31 00",
        options: ["Galvanized", "Aluminum", "Stainless"] },
      { key: "controls",      label: "Controls / BMS", type: "select", csi: "23 09 00",
        options: ["CS controls package", "Tie-in to house BMS", "Standalone", "By others"] },
      { key: "exhaust_cfm",   label: "Process exhaust (CFM)", type: "number", perRoom: true, csi: "23 38 00" },
      // HEPA engineering inputs (src/utils/roomSystemsEngine.js). Coverage is
      // the ceiling-area constraint (ISO 5 rooms want ~30–100% filter
      // coverage regardless of ACH math); the override is the estimator's
      // explicit "use N instead" — blank = calculated. Filter airflow/face
      // area come from the selected Terminal filtration option's Basis of
      // Design attributes, not from a field here.
      { key: "hepa_coverage_percent", label: "HEPA ceiling coverage (%)", type: "number", perRoom: true, csi: "23 40 00" },
      { key: "hepa_count_override",   label: "HEPA count override (blank = calculated)", type: "number", perRoom: true, csi: "23 40 00" },
      // Load-calc inputs for hvacLoadEngine.js (Methods 2/3 CFM, coil/humidifier/
      // fan sizing). Lighting is deliberately NOT a field here — it's read from
      // the Electrical discipline's lighting_fixture_count + VaPerLightingFixture
      // so a lighting change on the Electrical side is instantly reflected in
      // the heat-gain calc instead of needing a second, divergent entry.
      { key: "occupancy_count",   label: "Occupancy (people)", type: "number", perRoom: true, csi: "23 30 00" },
      { key: "exterior_wall_ft",  label: "Exterior wall (LF)", type: "number", perRoom: true, csi: "23 30 00" },
      { key: "supply_air_delta_t", label: "Supply air ΔT (°F)", type: "number", csi: "23 30 00" },
      { key: "safety_factor_pct",  label: "Safety factor (%)", type: "number", csi: "23 30 00" },
      { key: "outdoor_summer_db",  label: "Outdoor design temp, summer (°F DB)", type: "number", csi: "23 30 00" },
      { key: "outdoor_summer_rh",  label: "Outdoor design RH, summer (%)", type: "number", csi: "23 30 00" }, // scenario coil load: outside-air latent term (unresolved when blank)
      { key: "outdoor_winter_db",  label: "Outdoor design temp, winter (°F DB)", type: "number", csi: "23 30 00" },
      { key: "indoor_design_temp", label: "Indoor design temp (°F)", type: "number", csi: "23 30 00" },
      { key: "indoor_design_rh",   label: "Indoor design RH (%)", type: "number", csi: "23 30 00" },
      { key: "ahu_default_tsp",    label: "AHU total static pressure (in. WG)", type: "number", csi: "23 30 00" },
      { key: "fan_efficiency_pct", label: "Fan efficiency (%)", type: "number", csi: "23 30 00" },
      { key: "reheat_rise_default", label: "Reheat design rise (°F)", type: "number", csi: "23 30 00" },
    ],
  },
  {
    key: "elec",
    label: "Electrical",
    icon: "⚡",
    scopeDiscipline: "Electrical",
    fields: [
      { key: "lighting",     label: "Lighting fixtures", type: "select", csi: "26 51 00",
        options: ["LED flat panel (cleanroom rated)", "Teardrop", "By others"] },
      { key: "light_level",  label: "Light level", type: "select", csi: "26 51 00",
        options: ["60 fc", "80 fc", "100 fc"] },
      { key: "power_scope",  label: "Power distribution", type: "select", csi: "26 20 00",
        options: ["Full distribution by CS", "Connections only", "By others"] },
      { key: "fire_alarm",   label: "Fire alarm devices", type: "select", csi: "28 31 00",
        options: ["By CS", "By others"] },
      // Project design-basis demand factors by circuit load class (P1 Phase
      // D-3, product decision 2026-09-12). Project level only: a value here
      // wins over the CleanSpace standard (TakeoffVariables DEMAND_FACTOR_*,
      // db/91) and loses to a circuit's own override — panelLoads.js
      // resolveDemandFactor. Blank = standard. Never code-compliant sizing.
      { key: "demand_factor_receptacle", label: "Demand factor — receptacles (blank = CleanSpace standard)", type: "number", csi: "26 24 16" },
      { key: "demand_factor_lighting",   label: "Demand factor — lighting (blank = standard)",             type: "number", csi: "26 24 16" },
      { key: "demand_factor_motor",      label: "Demand factor — motors (blank = standard)",               type: "number", csi: "26 24 16" },
      { key: "demand_factor_equipment",  label: "Demand factor — equipment (blank = standard)",            type: "number", csi: "26 24 16" },
      { key: "demand_factor_hvac",       label: "Demand factor — HVAC (blank = standard)",                 type: "number", csi: "26 24 16" },
      { key: "outlet_count", label: "Duplex outlets", type: "number", perRoom: true, csi: "26 27 26" },
      // Since 2026-09-11 the fixture COUNT is calculated (lumen method +
      // spacing check, roomSystemsEngine.js) from light_level × the selected
      // lighting option's FixtureLumens. A room's own row here is an explicit
      // OVERRIDE of that calculation; the template/project-level value is
      // only a migration fallback for when the fixture option has no lumen
      // rating yet. Same key as before so no stored data moved.
      { key: "lighting_fixture_count", label: "Lighting fixtures (override — blank = calculated)", type: "number", perRoom: true, csi: "26 51 00" },
    ],
  },
  {
    key: "structural",
    label: "Structural",
    icon: "🏛️",
    scopeDiscipline: "Structural",
    projectWide: true,   // building-spanning systems: no per-room fields; quantities derive from totals
    fields: [
      { key: "support_steel",  label: "Ceiling support steel", type: "select", csi: "05 12 00",
        options: ["By CS", "By others", "Not required"] },
      { key: "seismic",        label: "Seismic bracing", type: "select", csi: "13 48 00",
        options: ["Design + install by CS", "Install only", "By others", "Not required"] },
      { key: "mezzanine",      label: "Mezzanine / platform", type: "select", csi: "05 51 00",
        options: ["None", "By CS", "By others"] },
    ],
  },
  {
    key: "fp",
    label: "Fire Protection",
    icon: "🚒",
    scopeDiscipline: "Plumbing & Fire Protection",
    projectWide: true,
    fields: [
      { key: "sprinkler_scope", label: "Sprinkler scope", type: "select", csi: "21 13 00",
        options: ["Head relocation only", "New coverage by CS", "Design-build by CS sub", "By others"] },
      { key: "sprinkler_type",  label: "System type", type: "select", csi: "21 13 00",
        options: ["Wet", "Dry", "Pre-action", "Existing to remain"] },
      { key: "plumbing_scope",  label: "Plumbing / drains", type: "select", csi: "22 13 00",
        options: ["By CS", "Connections only", "By others"] },
    ],
  },
  {
    key: "process",
    label: "Process & Utilities",
    icon: "🧪",
    scopeDiscipline: "Process & Utilities",
    fields: [
      { key: "piping_scope", label: "Process piping scope", type: "select", csi: "40 05 00",
        options: ["Piping by CS", "Stub-outs only", "By others"] },
      { key: "cda_drops",    label: "CDA drops", type: "number", perRoom: true, csi: "22 61 19" },
      { key: "n2_drops",     label: "N₂ drops", type: "number", perRoom: true, csi: "22 63 19" },
      { key: "vac_drops",    label: "Vacuum drops", type: "number", perRoom: true, csi: "22 62 19" },
      { key: "pw_drops",     label: "PW / DI drops", type: "number", perRoom: true, csi: "22 67 00" },
    ],
  },
];

// Basis of Design (db/62_basis_of_design_options.sql, Setup page
// BasisOfDesign.jsx) is the real, admin-managed source of truth for a
// select field's choices — field.options above is only the seeded/fallback
// default, kept in the file permanently so a picker never renders empty
// before the DB is seeded (or if every option for a field is retired).
// bodOptionsByField is a flat { "disc|fieldKey": [optionValue, ...] } map of
// ACTIVE options, built by each consumer from sp_GetBasisOfDesignOptions.
export function resolveFieldOptions(bodOptionsByField, discKey, field) {
  return bodOptionsByField?.[`${discKey}|${field.key}`] ?? field.options ?? [];
}

// Builds the { "disc|fieldKey": [optionValue, ...] } map resolveFieldOptions
// reads, from sp_GetBasisOfDesignOptions' raw rows (active only, already
// DisplayOrder-sorted by the proc). Shared by every consumer so the
// active-rows-only + grouping logic lives in exactly one place.
export function buildBodOptionsMap(rows) {
  const map = {};
  for (const r of rows || []) {
    if (!r.IsActive) continue;
    const key = `${r.Discipline}|${r.FieldKey}`;
    (map[key] || (map[key] = [])).push(r.OptionValue);
  }
  return map;
}

// Criteria rows come back as flat key/value rows; key them for O(1) lookup.
// RoomID null = project-level default.
export const criterionKey = (discipline, fieldKey, roomId) =>
  `${discipline}|${fieldKey}|${roomId ?? 0}`;

// Effective per-room value with the full inheritance chain. Returns
// { value, source } where source is 'override' | 'template' | 'project' | null.
//
// Room Templates Phase F-2: the legacy hardcoded ROOM_TYPE_DEFAULTS map
// (keyed by RoomType, ~11 count fields only) is retired now that every room
// has a RoomTemplate (Phase F-1 seeded + backfilled every existing room from
// the exact same legacy data, so this is not a behavior change for anything
// that already existed). Read off `room._templateDefaults` (a plain object
// keyed by "discipline|fieldKey", or null/undefined) rather than a new
// function parameter — the same convention as the pre-existing `room._cfm`
// (see RoomTakeoff.jsx/TakeoffViews.jsx's SystemView) — so every one of this
// function's existing call sites (roomScopeFormulas, hvacLoadEngine,
// electricalCircuitEngine, TakeoffCriteriaPanel, TakeoffViews) needed zero
// signature changes for this. A room with no template (or a caller that
// never attached _templateDefaults) now falls straight through to the
// project default.
export function effectiveCriterion(criteria, disc, fieldKey, room) {
  const own = criteria[criterionKey(disc, fieldKey, room.RoomID)];
  if (own !== undefined && own !== "") return { value: own, source: "override" };
  const templateDef = room._templateDefaults?.[`${disc}|${fieldKey}`];
  if (templateDef !== undefined && templateDef !== "") return { value: templateDef, source: "template" };
  const proj = criteria[criterionKey(disc, fieldKey, null)];
  if (proj !== undefined && proj !== "") return { value: proj, source: "project" };
  return { value: "", source: null };
}
